import type { TransportResultQL } from '@libs/trip-planner-interfaces';

import type { Money } from '../../general';
import type { TransportType } from '../../semboEndpoints/transport';
import type {
  HotelAlternativeOptionWithBasicRooms,
  HotelBookableOptionWithAlternativeRoom,
  HotelBookableOptionWithBasicRooms,
  ItineraryPrices,
} from '../../tripPlanner';

export interface ItineraryResponseQL {
  itineraryKey: string;
  result: (
    | HotelIngredientResponseByItineraryQL
    | TransportIngredientResponseQL
  )[];
  prices: ItineraryPrices;
}

export type IngredientResponseTypeQL =
  | 'HotelIngredientResponseQL'
  | 'TicketIngredientResponseQL'
  | 'TransportIngredientResponseQL';

export interface IngredientResponseByItineraryQL {
  subIngredientResults?: ActivityIngredientResponseByItineraryQL[];
  type: IngredientResponseTypeQL;
}

export type HotelIngredientResponseByItineraryQL =
  | AvailableHotelIngredientResponseByItineraryQL
  | UnavailableHotelIngredientResponseByItineraryQL;

export interface AvailableHotelIngredientResponseByItineraryQL
  extends IngredientResponseByItineraryQL {
  date: string;
  lengthOfStay: LengthOfStayQL;
  suggested: HotelBookableOptionWithBasicRooms;
  suggestions: HotelAlternativeOptionWithBasicRooms[];
  alternativeSuggestion: null;
}

export interface UnavailableHotelIngredientResponseByItineraryQL
  extends IngredientResponseByItineraryQL {
  date: string;
  lengthOfStay: LengthOfStayQL;
  suggested: null;
  suggestions: null;
  alternativeSuggestion: HotelBookableOptionWithAlternativeRoom | null;
}

interface LengthOfStayQL {
  numberOfNights: number;
  nightsHaveBeenSuggested: boolean;
}

export type ActivityIngredientResponseByItineraryQL =
  | AvailableActivityIngredientResponseByItineraryQL
  | UnavailableActivityIngredientResponseByItineraryQL;

export interface AvailableActivityIngredientResponseByItineraryQL
  extends IngredientResponseByItineraryQL {
  code: string;
  date: string;
  totalPrice: Money;
}

export interface UnavailableActivityIngredientResponseByItineraryQL
  extends IngredientResponseByItineraryQL {
  code: null;
  date: string;
  totalPrice: null;
}

export type TransportIngredientResponseQL =
  | AvailableTransportIngredientResponseQL
  | UnavailableTransportIngredientResponseQL;

export interface AvailableTransportIngredientResponseQL
  extends IngredientResponseByItineraryQL {
  addToBasketToken: string;
  date: string;
  allCarriers: string[];
  totalPrice: Money;
  legs: LegQL[];
  legIndex: number;
  alternativeSuggestion: null;
  durationRangeInMinutes?: DoubleRangeQL;
}

export interface DoubleRangeQL {
  from: number;
  to: number;
}

export interface UnavailableTransportIngredientResponseQL
  extends IngredientResponseByItineraryQL {
  addToBasketToken: null;
  date: string;
  allCarriers: null;
  totalPrice: null;
  legs: null;
  legIndex: number;
  alternativeSuggestion: TransportResultQL | null;
}

export interface LegQL {
  durationInMinutes: number;
  segments: SegmentQL[];
}

export interface SegmentQL {
  arrivalDateTime: Date;
  arrivalIata: string;
  carrier: string;
  departureDateTime: Date;
  departureIata: string;
  transportId: string;
  layoverTimeUntilNextSegmentInMinutes: number;
  operatingCarrier: string;
  transportType: TransportType;
  isCabinMandatory: boolean;
  transportationName?: string;
  durationInMinutes?: number;
  vehicles?: TransportVehicleTypeQL[];
}

export enum TransportVehicleTypeQL {
  Vehicle6mLong = 'VEHICLE_6_M_LONG',
  MotorBike = 'MOTOR_BIKE',
  Bicycle = 'BICYCLE',
  MiniBus4mHigh6mLong = 'MINI_BUS_4_M_HIGH_6_M_LONG',
  Vehicle4m70cmLong = 'VEHICLE_4_M_70_CM_LONG',
  MultiPurposeVehicle = 'MULTI_PURPOSE_VEHICLE',
  MiniBus8mLong = 'MINI_BUS_8_M_LONG',
  None = 'NONE',
}
